/**
 * Sprint 8 — Public landing page sections.
 *
 * All sections consume existing design-system components. Pricing content is
 * fetched from the authoritative GET /api/billing/plans at runtime via the
 * `PublicPricingStrip` component — no hardcoded prices.
 */
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import {
  ShoppingCart, Package, TrendingUp, Users, Wallet, BarChart3,
  Smartphone, Tablet, Monitor, Receipt, Wifi, WifiOff, Globe,
  Store, Coffee, UtensilsCrossed, Croissant, Wrench, Hammer,
  Scissors, Building2, ArrowUpRight, Check, Shield, Clock3,
  ChevronRight, Image as ImageIcon,
} from "lucide-react";

// ---------- Product capabilities (§5) ----------
const CAPABILITIES = [
  { icon: ShoppingCart, title: "POS", blurb: "Fast selling from your phone, tablet or computer." },
  { icon: Package, title: "Inventory", blurb: "Know what's in stock and what needs restocking." },
  { icon: TrendingUp, title: "Sales", blurb: "See every transaction and track performance over time." },
  { icon: Users, title: "Customers / Suki", blurb: "Build a simple customer list and spot your regulars." },
  { icon: Wallet, title: "Utang & Gastos", blurb: "Track customer balances and everyday business expenses." },
  { icon: BarChart3, title: "Reports", blurb: "See how your business is performing — without spreadsheets." },
];

export function CapabilitiesSection() {
  return (
    <section id="features" className="border-b border-border" data-testid="landing-capabilities">
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-20 lg:py-24">
        <div className="max-w-2xl">
          <div className="text-[10px] uppercase tracking-widest text-brand font-medium">What you get</div>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl tracking-tighter">
            Everything a Philippine small business needs.
          </h2>
          <p className="mt-3 text-base text-muted-foreground">
            One subscription. No add-ons needed to run your day-to-day.
          </p>
        </div>
        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {CAPABILITIES.map(({ icon: Icon, title, blurb }) => (
            <div key={title}
                 data-testid={`capability-${title.toLowerCase().replace(/[^a-z]/g, "-")}`}
                 className="rounded-xl border border-border bg-card p-5 hover:border-brand/40 hover:shadow-sm transition">
              <div className="h-10 w-10 rounded-lg bg-brand/10 flex items-center justify-center">
                <Icon className="h-5 w-5 text-brand" strokeWidth={1.75} />
              </div>
              <h3 className="mt-4 font-display text-lg tracking-tight">{title}</h3>
              <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{blurb}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---------- How It Works (§6) ----------
const STEPS = [
  { n: 1, title: "Create your business", blurb: "Choose your industry and set up BizOne in minutes." },
  { n: 2, title: "Add your products", blurb: "Import or enter products and inventory." },
  { n: 3, title: "Start selling", blurb: "Open POS from your phone, tablet or computer." },
  { n: 4, title: "Monitor your business", blurb: "Track sales, stocks, expenses and reports." },
];

export function HowItWorksSection() {
  return (
    <section id="how" className="border-b border-border bg-muted/30" data-testid="landing-how-it-works">
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-20 lg:py-24">
        <div className="max-w-2xl">
          <div className="text-[10px] uppercase tracking-widest text-brand font-medium">How it works</div>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl tracking-tighter">Up and running in 4 steps.</h2>
        </div>
        <ol className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {STEPS.map((s) => (
            <li key={s.n} className="relative rounded-xl border border-border bg-background p-5">
              <div className="h-9 w-9 rounded-full bg-brand text-brand-foreground font-display text-base flex items-center justify-center tabular-nums">
                {s.n}
              </div>
              <h3 className="mt-3 font-semibold text-base">{s.title}</h3>
              <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{s.blurb}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

// ---------- Hardware advantage (§7) ----------
export function HardwareSection() {
  const devices = [
    { icon: Smartphone, title: "Phone", blurb: "Sell and monitor from your Android or iPhone." },
    { icon: Tablet, title: "Tablet", blurb: "Perfect for the counter — big buttons, fast tap." },
    { icon: Monitor, title: "Laptop / Desktop", blurb: "Everything also works in your web browser." },
  ];
  const accessories = [
    { icon: Receipt, title: "Receipt printer" },
    { icon: Wallet, title: "Cash drawer" },
    { icon: Package, title: "Barcode scanner" },
  ];
  return (
    <section id="hardware" className="border-b border-border" data-testid="landing-hardware">
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-20 lg:py-24 grid gap-10 lg:grid-cols-2 items-start">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-brand font-medium">Hardware advantage</div>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl tracking-tighter">
            Use the devices you already have.
          </h2>
          <p className="mt-4 text-base text-muted-foreground max-w-md leading-relaxed">
            No expensive proprietary POS terminal required. Start selling with the phone, tablet or
            laptop that's already in your shop.
          </p>
          <div className="mt-6 space-y-3">
            {devices.map(({ icon: Icon, title, blurb }) => (
              <div key={title} className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-lg border border-border flex items-center justify-center shrink-0">
                  <Icon className="h-4 w-4" strokeWidth={1.75} />
                </div>
                <div>
                  <div className="font-semibold text-sm">{title}</div>
                  <div className="text-sm text-muted-foreground">{blurb}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-2xl border border-border bg-card p-6">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Optional accessories</div>
          <h3 className="mt-2 font-display text-xl tracking-tight">
            Connect supported accessories when your business needs them.
          </h3>
          <div className="mt-4 grid gap-3 sm:grid-cols-3">
            {accessories.map(({ icon: Icon, title }) => (
              <div key={title} className="rounded-lg border border-border p-4 text-center">
                <Icon className="h-6 w-6 mx-auto text-muted-foreground" strokeWidth={1.5} />
                <div className="mt-2 text-sm font-medium">{title}</div>
              </div>
            ))}
          </div>
          <p className="mt-4 text-xs text-muted-foreground leading-relaxed">
            Hardware is not included in the subscription. Compatibility applies to supported devices
            — see the How It Works page for the current list.
          </p>
        </div>
      </div>
    </section>
  );
}

// ---------- Offline (§8) ----------
export function OfflineSection() {
  return (
    <section className="border-b border-border bg-gradient-to-br from-brand/5 via-transparent to-brand/10"
             data-testid="landing-offline">
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-20 lg:py-24 grid gap-10 lg:grid-cols-5 items-center">
        <div className="lg:col-span-3">
          <div className="text-[10px] uppercase tracking-widest text-brand font-medium">Business continuity</div>
          <h2 className="mt-3 font-display text-3xl sm:text-5xl tracking-tighter">No Internet? Keep selling.</h2>
          <p className="mt-4 text-base text-muted-foreground max-w-lg leading-relaxed">
            BizOne can continue supporting key POS operations during temporary internet
            interruptions and synchronize supported data when connectivity returns.
          </p>
          <ul className="mt-5 space-y-2 text-sm">
            <li className="flex items-start gap-2"><Check className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" /> Previously authorized devices keep selling during temporary outages</li>
            <li className="flex items-start gap-2"><Check className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" /> Supported data syncs back when connectivity returns</li>
            <li className="flex items-start gap-2"><Check className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" /> New devices must authorize online first</li>
          </ul>
        </div>
        <div className="lg:col-span-2 flex justify-center">
          <div className="relative inline-flex items-center justify-center rounded-full border border-border bg-background w-56 h-56 shadow-sm">
            <div className="absolute inset-6 rounded-full border-2 border-dashed border-brand/40 animate-pulse" />
            <div className="relative flex flex-col items-center">
              <WifiOff className="h-10 w-10 text-brand" />
              <div className="mt-2 text-[10px] uppercase tracking-widest text-muted-foreground">Offline</div>
              <div className="font-display text-lg">Still selling</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ---------- Workspace branding (§9) ----------
export function WorkspaceBrandingSection() {
  return (
    <section className="border-b border-border" data-testid="landing-branding">
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-20 lg:py-24 grid gap-10 lg:grid-cols-2 items-center">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-brand font-medium">Your brand</div>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl tracking-tighter">
            Your business. Your brand. Your workspace.
          </h2>
          <p className="mt-4 text-base text-muted-foreground max-w-md leading-relaxed">
            Upload your logo, use your business name, and give your staff a branded entry at a
            short BizOne address they can remember.
          </p>
          <ul className="mt-5 space-y-2 text-sm">
            <li className="flex items-start gap-2"><Check className="h-4 w-4 text-brand mt-0.5 shrink-0" /> Upload your logo</li>
            <li className="flex items-start gap-2"><Check className="h-4 w-4 text-brand mt-0.5 shrink-0" /> Use your business name</li>
            <li className="flex items-start gap-2"><Check className="h-4 w-4 text-brand mt-0.5 shrink-0" /> Access your branded workspace entry</li>
          </ul>
        </div>
        <div>
          <div className="rounded-2xl border border-border bg-card p-6 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-lg bg-gradient-to-br from-brand to-violet-500 text-white flex items-center justify-center font-display text-lg">
                B
              </div>
              <div>
                <div className="font-semibold">Bella Amore Café</div>
                <div className="text-xs text-muted-foreground flex items-center gap-1">
                  <Globe className="h-3 w-3" /> bizonepos.store/w/bella-amore
                </div>
              </div>
            </div>
            <div className="mt-5 border-t border-border pt-5">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Who's working today</div>
              <div className="grid grid-cols-3 gap-2">
                {["JD", "MC", "RT"].map((n, i) => (
                  <div key={n} className="rounded-lg border border-border p-2 text-center">
                    <div className={`h-8 w-8 mx-auto rounded-full ${i === 0 ? "bg-amber-500/20" : i === 1 ? "bg-emerald-500/20" : "bg-blue-500/20"} grid place-items-center text-xs font-semibold`}>{n}</div>
                    <div className="mt-1 text-[10px] text-muted-foreground">Cashier</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ---------- Industries (§10) ----------
const INDUSTRIES = [
  { icon: Store, name: "Retail" },
  { icon: Coffee, name: "Coffee shops" },
  { icon: Coffee, name: "Cafés" },
  { icon: UtensilsCrossed, name: "Restaurants" },
  { icon: Croissant, name: "Bakeries" },
  { icon: Wrench, name: "Auto / Accessories" },
  { icon: Hammer, name: "Hardware" },
  { icon: Scissors, name: "Services" },
  { icon: Building2, name: "Other Philippine SMEs" },
];

export function IndustriesSection() {
  return (
    <section id="industries" className="border-b border-border bg-muted/30" data-testid="landing-industries">
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-20 lg:py-24">
        <div className="max-w-2xl">
          <div className="text-[10px] uppercase tracking-widest text-brand font-medium">Industries</div>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl tracking-tighter">One system. Adapted to your business.</h2>
          <p className="mt-3 text-base text-muted-foreground">
            Choose your industry and BizOne adjusts defaults and relevant features automatically.
          </p>
        </div>
        <div className="mt-10 grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-3">
          {INDUSTRIES.map(({ icon: Icon, name }) => (
            <div key={name} className="rounded-xl border border-border bg-background p-4 flex items-center gap-3">
              <div className="h-9 w-9 rounded-lg bg-muted flex items-center justify-center">
                <Icon className="h-4 w-4" strokeWidth={1.75} />
              </div>
              <div className="text-sm font-medium">{name}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---------- Staff / Operations (§11) ----------
export function StaffSection() {
  return (
    <section className="border-b border-border" data-testid="landing-staff">
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-20 lg:py-24">
        <div className="max-w-2xl">
          <div className="text-[10px] uppercase tracking-widest text-brand font-medium">Team controls</div>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl tracking-tighter">
            Give staff the access they need.
          </h2>
          <p className="mt-3 text-base text-muted-foreground">
            Cashiers can sell. Managers can monitor operations. Owners stay in control.
          </p>
        </div>
        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { icon: Users, title: "Staff accounts", blurb: "Create a login for every member of your team." },
            { icon: Shield, title: "Roles & permissions", blurb: "Owner, Admin, Manager, Cashier, Barista — fine-grained control." },
            { icon: Monitor, title: "Authorized devices", blurb: "Approve each device that signs in to your workspace." },
            { icon: Clock3, title: "Attendance", blurb: "Clock-in / clock-out on eligible plans. Branch controls on Business." },
          ].map(({ icon: Icon, title, blurb }) => (
            <div key={title} className="rounded-xl border border-border bg-card p-5">
              <div className="h-10 w-10 rounded-lg bg-brand/10 flex items-center justify-center">
                <Icon className="h-5 w-5 text-brand" strokeWidth={1.75} />
              </div>
              <h3 className="mt-3 font-semibold">{title}</h3>
              <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{blurb}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---------- Owner view marketing visual (§18) ----------
export function OwnerViewSection() {
  return (
    <section className="border-b border-border bg-gradient-to-br from-background to-brand/5"
             data-testid="landing-owner-view">
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-20 lg:py-24 grid gap-10 lg:grid-cols-2 items-center">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-brand font-medium">Owner view</div>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl tracking-tighter">
            Check your business from anywhere.
          </h2>
          <p className="mt-4 text-base text-muted-foreground max-w-md leading-relaxed">
            Open your phone and see today's sales, expenses, outstanding utang and low-stock
            alerts at a glance.
          </p>
        </div>
        <div className="rounded-2xl border border-border bg-card p-6 shadow-sm">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Demo preview</div>
          <div className="mt-3 grid grid-cols-2 gap-3">
            {[
              { label: "Today's Sales", value: "₱12,450", tone: "text-emerald-600 dark:text-emerald-400" },
              { label: "Transactions", value: "42" },
              { label: "Expenses", value: "₱1,850" },
              { label: "Outstanding Utang", value: "₱4,200" },
              { label: "Low Stock", value: "6 items", tone: "text-amber-600 dark:text-amber-400" },
              { label: "Open since", value: "7:30 AM" },
            ].map((k) => (
              <div key={k.label} className="rounded-lg border border-border p-3">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{k.label}</div>
                <div className={`mt-1 font-display text-xl tabular-nums ${k.tone || ""}`}>{k.value}</div>
              </div>
            ))}
          </div>
          <p className="mt-4 text-[10px] text-muted-foreground">Demo marketing figures only. Not real workspace data.</p>
        </div>
      </div>
    </section>
  );
}

// ---------- Pricing strip — authoritative API, graceful failure (§12–15) ----------
export function PublicPricingStrip() {
  const [plans, setPlans] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get("/billing/plans")
      .then(({ data }) => {
        const list = (data.plans || []).filter((p) => p.customer_facing !== false)
                                        .sort((a, b) => (a.order || 0) - (b.order || 0));
        setPlans(list);
      })
      .catch(() => setError(true));
  }, []);

  const money = (n) => "₱" + Number(n || 0).toLocaleString("en-PH", { maximumFractionDigits: 0 });

  return (
    <section id="pricing" className="border-b border-border" data-testid="landing-pricing">
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-20 lg:py-24">
        <div className="text-center max-w-2xl mx-auto">
          <div className="text-[10px] uppercase tracking-widest text-brand font-medium">Pricing</div>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl tracking-tighter">
            Affordable monthly pricing in pesos.
          </h2>
          <p className="mt-3 text-base text-muted-foreground">
            Start with a 7-day free trial. No payment required to start.
          </p>
        </div>

        {/* ERROR state — never show stale legacy hardcoded prices (§14) */}
        {error && !plans && (
          <div className="mt-10 rounded-xl border border-border bg-muted/40 p-8 text-center max-w-xl mx-auto"
               data-testid="pricing-unavailable">
            <div className="text-sm font-medium">Pricing is temporarily unavailable.</div>
            <p className="mt-2 text-sm text-muted-foreground">Please try again in a moment.</p>
            <div className="mt-4">
              <Link to="/signup"><Button size="sm" data-testid="pricing-fallback-cta">Start free trial</Button></Link>
            </div>
          </div>
        )}

        {/* LOADING skeleton */}
        {!error && !plans && (
          <div className="mt-10 grid gap-5 sm:grid-cols-3" data-testid="pricing-loading">
            {[0, 1, 2].map((i) => (
              <div key={i} className="rounded-xl border border-border p-6 animate-pulse h-56 bg-muted/40" />
            ))}
          </div>
        )}

        {/* PLANS */}
        {plans && plans.length > 0 && (
          <div className="mt-10 grid gap-5 sm:grid-cols-3" data-testid="pricing-grid">
            {plans.map((p) => (
              <div key={p.plan_id}
                   data-testid={`pricing-plan-${p.plan_id}`}
                   className={`rounded-2xl border p-6 bg-card ${p.popular ? "ring-2 ring-brand shadow-md" : ""}`}>
                {p.popular && (
                  <div className="text-[10px] uppercase tracking-widest text-brand font-semibold mb-2">
                    Most popular
                  </div>
                )}
                <h3 className="font-display text-xl tracking-tight">{p.name}</h3>
                <p className="mt-1 text-xs text-muted-foreground min-h-[32px]">{p.tagline}</p>
                <div className="mt-3 flex items-baseline gap-1">
                  <span className="font-display text-4xl tracking-tighter tabular-nums">{money(p.monthly_price)}</span>
                  <span className="text-sm text-muted-foreground">/mo</span>
                </div>
                <ul className="mt-4 space-y-1.5 min-h-[160px]">
                  {(p.included_features || []).slice(0, 5).map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm">
                      <Check className="h-3.5 w-3.5 mt-0.5 text-emerald-500 shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                <Link to={`/signup?plan=${p.plan_id}`}>
                  <Button className="mt-5 w-full" variant={p.popular ? "default" : "outline"}
                          data-testid={`pricing-plan-${p.plan_id}-cta`}>
                    Start free trial <ArrowUpRight className="h-3.5 w-3.5 ml-1" />
                  </Button>
                </Link>
              </div>
            ))}
          </div>
        )}

        <div className="mt-6 text-center text-xs text-muted-foreground">
          Pricing is loaded live from the BizOne catalog. Pay via GCash — verification required.
        </div>
      </div>
    </section>
  );
}

// ---------- Final CTA (§21) ----------
export function FinalCta() {
  return (
    <section className="bg-gradient-to-br from-brand via-brand to-violet-600 text-brand-foreground"
             data-testid="landing-final-cta">
      <div className="max-w-5xl mx-auto px-6 lg:px-10 py-20 lg:py-24 text-center">
        <h2 className="font-display text-3xl sm:text-5xl tracking-tighter">
          Ready to run your business with BizOne?
        </h2>
        <p className="mt-4 text-base sm:text-lg opacity-90">
          Start your 7-day free trial. No payment required.
        </p>
        <div className="mt-8 flex items-center justify-center gap-3 flex-wrap">
          <Link to="/signup">
            <Button size="lg" variant="secondary" className="gap-1.5"
                    data-testid="landing-final-cta-start">
              Start free trial <ArrowUpRight className="h-4 w-4" />
            </Button>
          </Link>
          <Link to="/pricing">
            <Button size="lg" variant="ghost"
                    className="text-brand-foreground hover:bg-white/10 border border-white/30"
                    data-testid="landing-final-cta-pricing">
              View pricing
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
}
